/**
 * Date range picker component for transaction filtering
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, useState, useEffect, useRef } from "react";
import {
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { formatDateForDisplay, formatDateLocal, getCalendarDays } from "../../../utils/transactionUtils";

export interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  focusedDateField: "startDate" | "endDate" | null;
  onFocusedDateFieldChange: (field: "startDate" | "endDate" | null) => void;
}

/**
 * Date range picker component with mobile calendar picker
 */
export default function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  focusedDateField,
  onFocusedDateFieldChange,
}: DateRangePickerProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [calendarViewDate, setCalendarViewDate] = useState<Date>(new Date());
  // Local state for immediate display update
  const [localStartDate, setLocalStartDate] = useState<string>(startDate);
  const [localEndDate, setLocalEndDate] = useState<string>(endDate);
  
  // Track if update is from internal change to prevent infinite loops
  const isInternalUpdateRef = useRef<{ startDate: boolean; endDate: boolean }>({ startDate: false, endDate: false });
  
  // Sync local state with props (only when props change externally, like clearing filters)
  // Skip sync if the change came from internal update to prevent infinite loops
  useEffect(() => {
    if (!isInternalUpdateRef.current.startDate && startDate !== localStartDate) {
      setLocalStartDate(startDate);
    }
    isInternalUpdateRef.current.startDate = false;
  }, [startDate]);
  
  useEffect(() => {
    if (!isInternalUpdateRef.current.endDate && endDate !== localEndDate) {
      setLocalEndDate(endDate);
    }
    isInternalUpdateRef.current.endDate = false;
  }, [endDate]);

  // Sync calendar view with selected date when date field is focused
  useEffect(() => {
    if (focusedDateField === "startDate" && localStartDate) {
      setCalendarViewDate(new Date(localStartDate));
    } else if (focusedDateField === "endDate" && localEndDate) {
      setCalendarViewDate(new Date(localEndDate));
    } else if (focusedDateField && !localStartDate && !localEndDate) {
      // If no date is set, use today's date
      setCalendarViewDate(new Date());
    }
  }, [focusedDateField, localStartDate, localEndDate]);

  // Close calendar when clicking outside (mobile only)
  useEffect(() => {
    if (!isMobile || !focusedDateField) return;

    const handleClickOutside = (event: Event) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside the calendar picker
      if (target.closest('[data-calendar-picker]')) {
        return;
      }
      // Close calendar
      onFocusedDateFieldChange(null);
    };

    // Use EventListener type which accepts Event (base type for both MouseEvent and TouchEvent)
    const handleMouseDown: EventListener = handleClickOutside;
    const handleTouchStart: EventListener = handleClickOutside;

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('touchstart', handleTouchStart);
    
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('touchstart', handleTouchStart);
    };
  }, [isMobile, focusedDateField, onFocusedDateFieldChange]);

  const handleCalendarDateSelect = (date: Date) => {
    const dateString = formatDateLocal(date);
    if (focusedDateField === "startDate") {
      isInternalUpdateRef.current.startDate = true;
      setLocalStartDate(dateString); // Update local state immediately
      onStartDateChange(dateString); // Update parent state to trigger filtering
    } else if (focusedDateField === "endDate") {
      isInternalUpdateRef.current.endDate = true;
      setLocalEndDate(dateString); // Update local state immediately
      onEndDateChange(dateString); // Update parent state to trigger filtering
    }
    // Close calendar immediately
    onFocusedDateFieldChange(null);
  };

  const handleCalendarMonthChange = (direction: "prev" | "next") => {
    const newDate = new Date(calendarViewDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCalendarViewDate(newDate);
  };

  const renderDateInput = (
    label: string,
    value: string,
    onChange: (date: string) => void,
    field: "startDate" | "endDate"
  ) => {
    if (isMobile) {
      return (
        <TextField
          type="text"
          label={label}
          value={formatDateForDisplay(value)}
          placeholder="mm/dd/yyyy"
          inputMode="none"
          autoComplete="off"
          focused={focusedDateField === field}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
            // Prevent any scrolling
            window.scrollTo(0, window.scrollY);
            onFocusedDateFieldChange(field);
          }}
          onFocus={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            (e.target as HTMLInputElement).blur();
            onFocusedDateFieldChange(field);
          }}
          onKeyDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            e.preventDefault();
            e.nativeEvent.stopImmediatePropagation();
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarTodayIcon 
                  sx={{ 
                    color: focusedDateField === field ? "primary.main" : "text.secondary",
                    fontSize: "1.25rem",
                  }} 
                />
              </InputAdornment>
            ),
          }}
          size="small"
          fullWidth
          sx={{
            width: "100%",
            minWidth: { xs: "140px", sm: "160px", md: "180px" },
            "& .MuiOutlinedInput-root": {
              cursor: "pointer",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              "& fieldset": {
                borderColor: focusedDateField === field ? "primary.main" : "rgba(0, 0, 0, 0.23)",
              },
              "&.Mui-focused fieldset": {
                borderColor: focusedDateField === field ? "primary.main" : "rgba(0, 0, 0, 0.23)",
              },
            },
            "& input": {
              cursor: "pointer",
              caretColor: "transparent",
              WebkitUserSelect: "none",
              userSelect: "none",
            },
          }}
        />
      );
    }

    // Desktop: Native date input
    return (
      <TextField
        type="date"
        label={label}
        value={value}
        onChange={(e) => {
          const newValue = e.target.value;
          // Mark as internal update to prevent infinite loop
          if (field === "startDate") {
            isInternalUpdateRef.current.startDate = true;
            setLocalStartDate(newValue);
          } else {
            isInternalUpdateRef.current.endDate = true;
            setLocalEndDate(newValue);
          }
          // Update parent state to trigger filtering
          onChange(newValue);
        }}
        InputLabelProps={{ shrink: true }}
        size="small"
        fullWidth
        placeholder="mm/dd/yyyy"
        sx={{
          width: "100%",
          minWidth: { xs: "140px", sm: "160px", md: "180px" },
          "& .MuiOutlinedInput-root": {
            width: "100%",
            minWidth: { xs: "140px", sm: "160px", md: "180px" },
            "& input": {
              width: "100%",
              minWidth: { xs: "140px", sm: "160px", md: "180px" },
              paddingRight: "8px",
            },
          },
          "& input[type='date']": {
            width: "100%",
            minWidth: 0,
            WebkitAppearance: "none",
            "&::-webkit-calendar-picker-indicator": {
              position: "absolute",
              right: "8px",
              width: "20px",
              height: "20px",
              cursor: "pointer",
            },
            "&::-webkit-inner-spin-button": {
              display: "none",
            },
            "&::-webkit-clear-button": {
              display: "none",
            },
            "&::placeholder": {
              color: "text.secondary",
              opacity: 0.6,
            },
          },
        }}
        InputProps={{
          sx: {
            width: "100%",
            minWidth: 0,
          },
        }}
      />
    );
  };

  // Return both date inputs in a Box for proper layout
  return (
    <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
      <Box sx={{ flex: 1, minWidth: { xs: "140px", sm: "160px", md: "180px" } }}>
        {renderDateInput("Start Date", localStartDate, onStartDateChange, "startDate")}
      </Box>
      <Box sx={{ flex: 1, minWidth: { xs: "140px", sm: "160px", md: "180px" } }}>
        {renderDateInput("End Date", localEndDate, onEndDateChange, "endDate")}
      </Box>
      
      {/* Calendar Picker Modal - Mobile only */}
      {isMobile && focusedDateField && (
        <Box
          data-calendar-picker
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
          onClick={() => onFocusedDateFieldChange(null)}
        >
          <Paper
            elevation={8}
            sx={{
              width: "100%",
              maxWidth: "400px",
              maxHeight: "90vh",
              overflow: "auto",
              bgcolor: "background.paper",
              borderRadius: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <IconButton
                  onClick={() => {
                    handleCalendarMonthChange("prev");
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h6">
                  {new Date(calendarViewDate).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                </Typography>
                <IconButton
                  onClick={() => {
                    handleCalendarMonthChange("next");
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 1,
                  mb: 2,
                }}
              >
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <Typography key={day} variant="caption" sx={{ textAlign: "center", fontWeight: "bold" }}>
                    {day}
                  </Typography>
                ))}
                {getCalendarDays(new Date(calendarViewDate).getFullYear(), new Date(calendarViewDate).getMonth()).map((day, index) => {
                  const isCurrentMonth = day.getMonth() === new Date(calendarViewDate).getMonth();
                  const dayString = formatDateLocal(day);
                  const isSelected = focusedDateField === "startDate" && localStartDate && dayString === localStartDate ||
                                    focusedDateField === "endDate" && localEndDate && dayString === localEndDate;
                  const isInRange = localStartDate && localEndDate && dayString >= localStartDate && dayString <= localEndDate;
                  const isToday = day.toDateString() === new Date().toDateString();

                  return (
                    <Button
                      key={index}
                      onClick={() => handleCalendarDateSelect(day)}
                      sx={{
                        minWidth: "auto",
                        aspectRatio: "1",
                        p: 0,
                        color: isCurrentMonth ? "text.primary" : "text.disabled",
                        bgcolor: isSelected ? "primary.main" : isInRange ? "primary.light" : "transparent",
                        "&:hover": {
                          bgcolor: isSelected ? "primary.dark" : isInRange ? "primary.main" : "action.hover",
                        },
                        border: isToday ? 2 : 0,
                        borderColor: "primary.main",
                      }}
                    >
                      {day.getDate()}
                    </Button>
                  );
                })}
              </Box>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  handleCalendarDateSelect(new Date());
                }}
              >
                Today
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
